/**
 * Lane planner "Saved" tab: saved quotes as the same cards as the results,
 * with search, filters and a sort, then a collapsed "Expired" group. Saved
 * searches follow in their own collapsed group so they can still be run or
 * removed (the Search tab lists them for quick loading).
 *
 * Data is Zoomlogi-side history, so the tab works even though vendors keep
 * searches only briefly. "Run" on a saved search and "Load rates" on a saved
 * quote both re-run the stored search request through the normal search flow:
 * rates expire quickly, so re-running the lane is how saved entries load
 * fresh rates. Entries move to the "Expired" group when they can no longer be
 * acted on (quote past its validity, search past its ship date). Removing an
 * entry unpins/archives it; history rows are never hard-deleted.
 */
import React, { useEffect, useRef, useState } from 'react';
import { message, Spin } from 'antd';
import dayjs from 'dayjs';

import {
  DeleteSavedLaneQuote,
  DeleteSavedLaneSearch,
  GetSavedLaneQuotes,
  GetSavedLaneSearches,
  LaneMode,
  LanePage,
  LaneSearchRequest,
  laneErrorMessage,
  SavedLaneQuote,
  SavedLaneSearch,
  unwrapLanePayload,
} from '../../../apis/lanePlanner';
import { AddFilter, FilterChips } from './AddFilter';
import QuoteCard from './QuoteCard';
import TruckIcon from './TruckIcon';
import { formatSearchCargo, quoteExpired, searchExpired } from './savedFormat';
import {
  applySavedFilters,
  SAVED_SORT_OPTIONS,
  SavedSort,
  savedFilterGroups,
  searchSaved,
  sortSaved,
  vendorLabel,
} from './savedView';
import { quoteShareText } from './quoteShare';
import { MODE_TITLES } from './quoteView';
import { pruneFilters, type FilterState } from './resultsView';
import styles from './styles.module.css';

/** Most rows one list request returns (the server's cap). */
const PAGE_LIMIT = 100;

interface SavedPaneProps {
  token: string;
  /** Bumps whenever something new is saved so the lists refetch. */
  refreshKey: number;
  onRunSearch: (request: LaneSearchRequest) => void;
  /** Called after a remove so the Search tab's dropdown stays in sync. */
  onChanged?: () => void;
  /** Clears the Search tab's "Saved" button state when a quote is removed. */
  onQuoteRemoved?: (quoteId: string) => void;
}

const formatLane = (
  origin?: string | null,
  destination?: string | null
): string => [origin || '?', destination || '?'].join(' → ');

const formatDay = (iso?: string | null): string | null =>
  iso ? dayjs(iso).format('MMM D') : null;

const modesLabel = (modes: LaneMode[]): string =>
  modes.map((mode) => MODE_TITLES[mode]).join(' + ');

const PlaneIcon: React.FC = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
    <path
      d='M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5Z'
      fill='currentColor'
    />
  </svg>
);

const ShipIcon: React.FC = () => (
  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
    <path
      d='M4 11V6h4V4h8v2h4v5l-2 6H6l-2-6Z'
      stroke='currentColor'
      strokeWidth='1.6'
      fill='none'
    />
    <path
      d='M2 20c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0'
      stroke='currentColor'
      strokeWidth='1.6'
      fill='none'
    />
  </svg>
);

const MODE_ICONS: Record<LaneMode, React.ReactNode> = {
  air: <PlaneIcon />,
  ocean: <ShipIcon />,
  road: <TruckIcon />,
};

const ModeIcon: React.FC<{ mode: LaneMode }> = ({ mode }) => (
  <span className={styles.savedIcon}>{MODE_ICONS[mode]}</span>
);

const SavedPane: React.FC<SavedPaneProps> = ({
  token,
  refreshKey,
  onRunSearch,
  onChanged,
  onQuoteRemoved,
}) => {
  const [loading, setLoading] = useState(true);
  const [searches, setSearches] = useState<SavedLaneSearch[]>([]);
  const [quotes, setQuotes] = useState<SavedLaneQuote[]>([]);
  const [expiredOpen, setExpiredOpen] = useState(false);
  const [searchesOpen, setSearchesOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [sort, setSort] = useState<SavedSort>('last_added');
  const [searchText, setSearchText] = useState('');
  const [searchInputOpen, setSearchInputOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [searchCursor, setSearchCursor] = useState<string | null>(null);
  const [quoteCursor, setQuoteCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadFailedCompletely, setLoadFailedCompletely] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const isFirstLoad = useRef(true);
  const loadedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const identityChanged = loadedTokenRef.current !== token;
    loadedTokenRef.current = token;
    // Clear prior company rows on identity change; same-company refreshes
    // keep cards visible (no flash) while the new fetch completes.
    if (identityChanged) {
      setSearches([]);
      setQuotes([]);
      isFirstLoad.current = true;
    }
    if (isFirstLoad.current) setLoading(true);
    setLoadError(null);
    setLoadFailedCompletely(false);
    const _ = Promise.allSettled([
      GetSavedLaneSearches(token, PAGE_LIMIT),
      GetSavedLaneQuotes(token, PAGE_LIMIT),
    ]).then(([searchesRes, quotesRes]) => {
      if (cancelled) return;
      let failed = 0;
      try {
        if (searchesRes.status !== 'fulfilled') throw searchesRes.reason;
        const page = unwrapLanePayload<LanePage<SavedLaneSearch>>(
          searchesRes.value
        );
        setSearches(page.items);
        setSearchCursor(page.has_more ? page.next_cursor : null);
      } catch {
        failed += 1;
      }
      try {
        if (quotesRes.status !== 'fulfilled') throw quotesRes.reason;
        const page = unwrapLanePayload<LanePage<SavedLaneQuote>>(
          quotesRes.value
        );
        setQuotes(page.items);
        setQuoteCursor(page.has_more ? page.next_cursor : null);
      } catch {
        failed += 1;
      }
      if (failed === 2) {
        setLoadFailedCompletely(true);
        setLoadError('Could not load saved items. Please try again.');
      } else if (failed > 0) {
        setLoadError('Some saved items could not be loaded. Please try again.');
      }
      setLoading(false);
      isFirstLoad.current = false;
    });
    return () => {
      cancelled = true;
    };
  }, [token, refreshKey]);

  /** Next page of whichever list has more, appended without repeats. */
  const handleLoadMore = async (): Promise<void> => {
    setLoadingMore(true);
    setActionError(null);
    const append = <T extends { id: string }>(rows: T[], more: T[]): T[] => [
      ...rows,
      ...more.filter((row) => !rows.some((r) => r.id === row.id)),
    ];
    try {
      if (searchCursor) {
        const page = unwrapLanePayload<LanePage<SavedLaneSearch>>(
          await GetSavedLaneSearches(token, PAGE_LIMIT, searchCursor)
        );
        setSearches((rows) => append(rows, page.items));
        setSearchCursor(page.has_more ? page.next_cursor : null);
      }
      if (quoteCursor) {
        const page = unwrapLanePayload<LanePage<SavedLaneQuote>>(
          await GetSavedLaneQuotes(token, PAGE_LIMIT, quoteCursor)
        );
        setQuotes((rows) => append(rows, page.items));
        setQuoteCursor(page.has_more ? page.next_cursor : null);
      }
    } catch (err) {
      setActionError(laneErrorMessage(err, 'Could not load more saved items.'));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRemoveSearch = async (id: string): Promise<void> => {
    setRemovingId(id);
    setActionError(null);
    try {
      await DeleteSavedLaneSearch(token, id);
      setSearches((rows) => rows.filter((r) => r.id !== id));
      onChanged?.();
    } catch (err) {
      setActionError(
        laneErrorMessage(err, 'Could not remove the saved search.')
      );
    } finally {
      setRemovingId(null);
    }
  };

  const handleRemoveQuote = async (id: string): Promise<void> => {
    setRemovingId(id);
    setActionError(null);
    const removed = quotes.find((r) => r.id === id);
    try {
      await DeleteSavedLaneQuote(token, id);
      setQuotes((rows) => rows.filter((r) => r.id !== id));
      if (removed) onQuoteRemoved?.(removed.quote.id);
      onChanged?.();
    } catch (err) {
      setActionError(
        laneErrorMessage(err, 'Could not remove the saved quote.')
      );
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.savedLoading}>
        <Spin />
      </div>
    );
  }

  if (loadFailedCompletely && loadError) {
    return (
      <div className={`${styles.savedSection} ${styles.savedSectionPadded}`}>
        <div className={styles.errorBanner}>{loadError}</div>
      </div>
    );
  }

  const groups = savedFilterGroups(quotes);
  // A filter whose chip is no longer offered must not keep hiding quotes.
  const activeFilters = pruneFilters(filters, groups);
  const visibleQuotes = sortSaved(
    searchSaved(applySavedFilters(quotes, activeFilters), searchText),
    sort
  );
  const activeQuotes = visibleQuotes.filter((q) => !quoteExpired(q));
  const expiredQuotes = visibleQuotes.filter(quoteExpired);
  const activeSearches = searches.filter((s) => !searchExpired(s));
  const expiredSearches = searches.filter(searchExpired);
  const empty = searches.length === 0 && quotes.length === 0;
  const filtering =
    Object.values(activeFilters).some((v) => v.length) || !!searchText;

  const handleShare = async (saved: SavedLaneQuote): Promise<void> => {
    try {
      await navigator.clipboard.writeText(
        quoteShareText(
          saved.quote,
          saved.origin,
          saved.destination,
          saved.ship_date
        )
      );
      message.success('Quote summary copied to the clipboard.');
    } catch {
      message.error('Could not copy the quote summary.');
    }
  };

  const renderQuote = (
    saved: SavedLaneQuote,
    expired: boolean
  ): React.ReactNode => (
    <QuoteCard
      key={saved.id}
      quote={saved.quote}
      vendorName={vendorLabel(saved.quote.vendor)}
      shipDate={saved.ship_date}
      expanded={expandedId === saved.id}
      onToggle={() =>
        setExpandedId((current) => (current === saved.id ? null : saved.id))
      }
      onShare={() => handleShare(saved)}
      extraActions={
        <>
          <button
            type='button'
            className={styles.shareLink}
            disabled={removingId === saved.id}
            onClick={() => handleRemoveQuote(saved.id)}
          >
            Remove
          </button>
          {!expired && (
            <button
              type='button'
              className={styles.secondaryAction}
              onClick={() => onRunSearch(saved.search_params)}
            >
              Load rates
            </button>
          )}
        </>
      }
    />
  );

  const renderSearchCard = (
    saved: SavedLaneSearch,
    expired: boolean
  ): React.ReactNode => {
    const cargo = formatSearchCargo(saved.params);
    return (
      <div
        key={saved.id}
        className={`${styles.savedCard} ${expired ? styles.savedCardExpired : ''}`}
      >
        <ModeIcon mode={saved.modes[0]} />
        <div className={styles.savedCardMain}>
          <div className={styles.savedCardTitle}>
            {saved.saved_name || formatLane(saved.origin, saved.destination)}
          </div>
          <div className={styles.savedCardSub}>
            {modesLabel(saved.modes)} ·{' '}
            {formatLane(saved.origin, saved.destination)}
            {saved.ship_date ? ` · ships ${formatDay(saved.ship_date)}` : ''}
            {cargo ? ` · ${cargo}` : ''}
          </div>
        </div>
        <div className={styles.savedCardActions}>
          <button
            type='button'
            className={styles.savedGhostButton}
            disabled={removingId === saved.id}
            onClick={() => handleRemoveSearch(saved.id)}
          >
            Remove
          </button>
          {!expired && (
            <button
              type='button'
              className={styles.savedRunButton}
              onClick={() => onRunSearch(saved.params)}
            >
              Run
            </button>
          )}
        </div>
      </div>
    );
  };

  const groupToggle = (
    label: string,
    open: boolean,
    toggle: () => void
  ): React.ReactNode => (
    <button
      type='button'
      className={styles.expiredToggle}
      aria-expanded={open}
      onClick={toggle}
    >
      {label}
      <svg
        width='10'
        height='10'
        viewBox='0 0 12 12'
        fill='none'
        className={`${styles.expandedChevron} ${
          open ? styles.expandedChevronOpen : ''
        }`}
      >
        <path
          d='M4.5 2.5 8 6l-3.5 3.5'
          stroke='currentColor'
          strokeWidth='1.6'
          fill='none'
        />
      </svg>
    </button>
  );

  return (
    <div className={`${styles.savedSection} ${styles.savedSectionPadded}`}>
      {loadError && (
        <div className={`${styles.errorBanner} ${styles.bannerSpaced}`}>
          {loadError}
        </div>
      )}
      {actionError && (
        <div className={`${styles.errorBanner} ${styles.bannerSpaced}`}>
          {actionError}
        </div>
      )}
      {empty && !loadError && (
        <div className={styles.emptyState}>
          Nothing saved yet. Run a search and use “Save search”, or save a quote
          from the results — they will show up here.
        </div>
      )}

      {quotes.length > 0 && (
        <>
          <div className={styles.resultsBar}>
            <button
              type='button'
              className={styles.savedSearchIcon}
              aria-label='Search saved quotes'
              aria-expanded={searchInputOpen}
              onClick={() => setSearchInputOpen((open) => !open)}
            >
              <svg width='14' height='14' viewBox='0 0 14 14' fill='none'>
                <circle
                  cx='6'
                  cy='6'
                  r='4'
                  stroke='currentColor'
                  strokeWidth='1.4'
                />
                <path d='m9 9 3 3' stroke='currentColor' strokeWidth='1.4' />
              </svg>
            </button>
            {searchInputOpen && (
              <input
                type='search'
                className={styles.savedSearchInput}
                aria-label='Search text'
                placeholder='Carrier, vendor, ref or lane'
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            )}
            <AddFilter
              groups={groups}
              value={activeFilters}
              onChange={setFilters}
            />
            <FilterChips
              groups={groups}
              value={activeFilters}
              onChange={setFilters}
            />
            <div className={styles.resultsBarEnd}>
              <label className={styles.sortLabel}>
                Sort by:{' '}
                <select
                  className={styles.sortSelect}
                  value={sort}
                  aria-label='Sort by'
                  onChange={(e) => setSort(e.target.value as SavedSort)}
                >
                  {SAVED_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className={styles.rateList}>
            {activeQuotes.map((saved) => renderQuote(saved, false))}
          </div>
          {filtering && visibleQuotes.length === 0 && (
            <div className={styles.emptyState}>
              No saved quotes match the search or filters.
            </div>
          )}
          {expiredQuotes.length > 0 && (
            <>
              {groupToggle(
                `Expired (${expiredQuotes.length})`,
                expiredOpen,
                () => setExpiredOpen((open) => !open)
              )}
              {expiredOpen && (
                <div className={styles.rateList}>
                  {expiredQuotes.map((saved) => renderQuote(saved, true))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {searches.length > 0 && (
        <>
          {groupToggle(
            `Saved searches (${searches.length})`,
            searchesOpen,
            () => setSearchesOpen((open) => !open)
          )}
          {searchesOpen && (
            <>
              {activeSearches.map((saved) => renderSearchCard(saved, false))}
              {expiredSearches.map((saved) => renderSearchCard(saved, true))}
            </>
          )}
        </>
      )}

      {(searchCursor || quoteCursor) && (
        <button
          type='button'
          className={styles.seeMore}
          disabled={loadingMore}
          onClick={() => handleLoadMore()}
        >
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
};

export default SavedPane;
